import { ExternalLink, FileText, PanelRightClose, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState, useSyncExternalStore } from "react";

/**
 * The supplier's document, shown without leaving the page (Tyler, 2026-09-24).
 *
 * Three ways, by how much room there is and what the brewer is doing:
 *   - reviewing an invoice on a wide screen, the document sits beside the passes, so the line being
 *     matched and the specs being entered can be read off the page they came from;
 *   - on a narrower one, hovering the document's name previews it, where there is a pointer to hover;
 *   - anywhere, pressing it opens it as a layer over the page, closed by clicking outside it, the ×
 *     in the corner, or Escape. A new tab is still one link away, for printing or saving.
 */

export interface DocumentRef {
  /** Where it is served from: `/api/v1/invoices/{id}/file`. */
  url: string;
  filename: string;
  contentType?: string | null;
}

export const invoiceDocument = (
  invoiceId: string,
  file: { filename: string; contentType?: string | null },
): DocumentRef => ({
  url: `/api/v1/invoices/${invoiceId}/file`,
  filename: file.filename,
  contentType: file.contentType ?? null,
});

const isImage = (doc: DocumentRef) => /^image\//.test(doc.contentType ?? "");

/**
 * The document itself. A PDF opens in the browser's own viewer, fitted to the width it has; an image
 * is shown whole. Nothing is converted or re-rendered: this is the file exactly as it arrived.
 */
export function DocumentFrame({ doc, compact = false }: { doc: DocumentRef; compact?: boolean }) {
  if (isImage(doc)) {
    return <img className="document-image" src={doc.url} alt={doc.filename} />;
  }
  // The viewer's own toolbar is noise in a preview; kept where the document has room to be used.
  const view = compact ? "#toolbar=0&navpanes=0&view=FitH" : "#navpanes=0&view=FitH";
  return <iframe className="document-frame" src={`${doc.url}${view}`} title={doc.filename} />;
}

/** Whether a media query holds, following it as the window changes. */
export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const list = window.matchMedia?.(query);
      list?.addEventListener("change", notify);
      return () => list?.removeEventListener("change", notify);
    },
    () => window.matchMedia?.(query).matches ?? false,
    () => false,
  );
}

/** The document as a layer over the page. Outside it, the ×, or Escape closes it. */
export function DocumentOverlay({
  doc,
  open,
  onClose,
}: {
  doc: DocumentRef;
  open: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="document-overlay"
      aria-label={doc.filename}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // A click that lands on the dialog itself, not its panel, is a click on the backdrop.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={() => {}}
    >
      {open ? (
        <div className="document-overlay-panel">
          <div className="document-overlay-head">
            <FileText size={16} aria-hidden />
            <strong className="document-name">{doc.filename}</strong>
            <a className="button small" href={doc.url} target="_blank" rel="noreferrer">
              <ExternalLink size={14} aria-hidden /> New tab
            </a>
            <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="document-overlay-body">
            <DocumentFrame doc={doc} />
          </div>
        </div>
      ) : null}
    </dialog>
  );
}

/**
 * The document's name, as a control. Pressing it opens the document over the page. Where asked for,
 * and where there is a pointer that can hover, resting on it previews the document in place — the
 * way to glance at a narrow screen's invoice without losing the line being matched.
 */
export function DocumentLink({
  doc,
  preview = false,
  className = "page-head-file",
  children,
}: {
  doc: DocumentRef;
  preview?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const canHover = useMedia("(hover: hover) and (pointer: fine)");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hovering = preview && canHover;
  const later = (show: boolean, ms: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPeek(show), ms);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <span className="document-link-wrap">
      <button
        type="button"
        className={`${className} document-link`}
        onClick={() => {
          clearTimeout(timer.current);
          setPeek(false);
          setOpen(true);
        }}
        onMouseEnter={hovering ? () => later(true, 300) : undefined}
        onMouseLeave={hovering ? () => later(false, 150) : undefined}
        onFocus={hovering ? () => later(true, 300) : undefined}
        onBlur={hovering ? () => later(false, 0) : undefined}
        title={`Open ${doc.filename}`}
      >
        {children ?? (
          <>
            <FileText size={13} aria-hidden /> {doc.filename}
          </>
        )}
      </button>
      {hovering && peek && !open ? (
        <div className="document-peek" role="tooltip">
          <DocumentFrame doc={doc} compact />
        </div>
      ) : null}
      <DocumentOverlay doc={doc} open={open} onClose={() => setOpen(false)} />
    </span>
  );
}

/**
 * The document beside the review, on a screen wide enough for both. Sticky, so it stays in view while
 * the passes scroll; put away with one press, and each device remembers which it prefers.
 */
export function DocumentPane({ doc, onHide }: { doc: DocumentRef; onHide: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <aside className="review-document" aria-label="The invoice document">
      <div className="review-document-head">
        <FileText size={15} aria-hidden />
        <span className="document-name">{doc.filename}</span>
        <button type="button" className="button small" onClick={() => setOpen(true)}>
          Larger
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={onHide}
          aria-label="Put the document away"
          title="Put the document away"
        >
          <PanelRightClose size={17} />
        </button>
      </div>
      <div className="review-document-body">
        <DocumentFrame doc={doc} />
      </div>
      <DocumentOverlay doc={doc} open={open} onClose={() => setOpen(false)} />
    </aside>
  );
}

const PANE_KEY = "bl.review.document";

/** Whether this device keeps the document beside the review. Shown unless put away. */
export function useDocumentPane(): [boolean, (shown: boolean) => void] {
  const [shown, setShown] = useState(() => {
    try {
      return localStorage.getItem(PANE_KEY) !== "hidden";
    } catch {
      return true;
    }
  });
  const set = (next: boolean) => {
    setShown(next);
    try {
      localStorage.setItem(PANE_KEY, next ? "shown" : "hidden");
    } catch {
      // Private browsing: remembered for this visit only.
    }
  };
  return [shown, set];
}
